import React, {
  useRef,
} from 'react';
import CurrencyTextField from 'core/components/CurrencyTextField';
import Tag from 'core/components/Tag';
import ToolTip from 'core/components/Tooltip';
import {
  FeeAmount,
  FeeScheduleLine,
  FeeScheduleProps,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/FeeSchedule/types';
import {
notEmptyString,
} from '@populateui/utils';

type FeeTextCellProps = {
  value?: string | null;
};

type FeeChargeCellProps = {
  feeLine: FeeScheduleLine;
  feeIndex: number;
  value: FeeScheduleLine[];
  onChange: FeeScheduleProps['onChange'];
  disabled: boolean;
  error: boolean;
};

export function isValidFeeAmount(chargeAmount?: FeeAmount) {
  return notEmptyString(chargeAmount) && Number(chargeAmount) > 0;
}

function FeeCodeCell({ value }: FeeTextCellProps) {
  return (
    <Tag
      defaultColor="bg-blue-100"
      className="max-w-full truncate !text-blue-700 font-semibold"
    >
      {value}
    </Tag>
  );
}

function FeeDescriptionCell({ value }: FeeTextCellProps) {
  const descriptionRef = useRef<HTMLSpanElement>(null);

  return (
    <ToolTip
      title={value ?? ''}
      auto
      ref={descriptionRef}
    >
      <span
        ref={descriptionRef}
        className="block w-full min-w-0 truncate text-xs text-gray-700"
      >
        {value}
      </span>
    </ToolTip>
  );
}

function FeeChargeCell({
  feeLine,
  feeIndex,
  value,
  onChange,
  disabled,
  error,
}: FeeChargeCellProps) {
  function handleChange(chargeAmount?: FeeAmount) {
    onChange(value.map((currentFeeLine, currentIndex) => (
      currentIndex === feeIndex
        ? { ...currentFeeLine, chargeAmount }
        : currentFeeLine
    )));
  }

  return (
    <CurrencyTextField
      id={`fee-schedule-${feeIndex}-charge`}
      className="w-full"
      inputProps={{
        'aria-label': `${feeLine.serviceCode} charge`,
      }}
      value={feeLine.chargeAmount}
      defaultValue={null}
      disabled={disabled}
      error={error}
      nullIfEmpty
      showErrorText={false}
      onChange={handleChange}
    />
  );
}

export default function FeeSchedule({
  value,
  onChange,
  disabled = false,
  error = false,
}: FeeScheduleProps) {
  return (
    <div className="w-full overflow-hidden">
      <div className="flex h-10 items-center bg-gray-100 text-xs font-semibold text-gray-600">
        <div className="w-[105px] shrink-0 px-3">Code</div>
        <div className="min-w-0 flex-1 px-3">Description</div>
        <div className="w-40 shrink-0 px-3">Price</div>
      </div>
      {value.map((feeLine, feeIndex) => (
        <div
          key={feeLine.serviceCode}
          className="flex h-14 items-center border-t border-gray-200"
        >
          <div className="flex w-[105px] shrink-0 items-center overflow-hidden px-3">
            <FeeCodeCell value={feeLine.serviceCode} />
          </div>
          <div className="flex min-w-0 flex-1 items-center overflow-hidden px-3">
            <FeeDescriptionCell value={feeLine.description} />
          </div>
          <div className="flex w-40 shrink-0 items-center px-3">
            <FeeChargeCell
              feeLine={feeLine}
              feeIndex={feeIndex}
              value={value}
              onChange={onChange}
              disabled={disabled}
              error={error && !isValidFeeAmount(feeLine.chargeAmount)}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
